import { computed, ref } from "vue";

/**
 * Installing the tenant app.
 *
 * Chromium fires `beforeinstallprompt` once the app meets the installability
 * bar (https, a real manifest with 192+512 icons — see ./manifest.ts — and a
 * service worker with a fetch handler) and the app is not already installed.
 * The event MUST be captured and re-fired from a user gesture — there is no way
 * to ask for it later — so the listener is registered at boot and the event
 * parked here. Its default (Chrome's own mini-infobar) is suppressed because
 * the shell shows its own offer: the install banner (shell/InstallBanner.vue)
 * and the account-menu row.
 *
 * Safari fires nothing: iOS installs through Share → "Add to Home Screen"
 * only, so on iOS the banner shows that instruction instead of a button.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** localStorage key that remembers "Not now" on the install banner, per origin. */
export const INSTALL_DISMISSED_KEY = "metaship.install.dismissed";

let deferred: BeforeInstallPromptEvent | null = null;

/** Chromium is holding a prompt for this origin. */
export const canInstall = ref(false);
/** `appinstalled` fired in this tab. */
export const installedNow = ref(false);
/** The person said "Not now" to the banner (remembered across visits). */
export const installDismissed = ref(readDismissed());

export type InstallOffer = "prompt" | "ios-hint" | null;

/**
 * What, if anything, the banner offers. Pure, so every state is testable:
 * nothing once the app runs installed or was just installed, nothing after a
 * dismissal, the real button whenever Chromium holds a prompt, and the
 * Share → Add to Home Screen hint on iOS (which never fires a prompt).
 */
export function installOfferFor(state: {
  canPrompt: boolean;
  ios: boolean;
  standalone: boolean;
  installed: boolean;
  dismissed: boolean;
}): InstallOffer {
  if (state.standalone || state.installed || state.dismissed) return null;
  if (state.canPrompt) return "prompt";
  if (state.ios) return "ios-hint";
  return null;
}

/** iPhone, iPod, or iPad — including iPadOS, which reports itself as a Mac with touch. */
export function isIos(nav: Pick<Navigator, "userAgent"> & { platform?: string; maxTouchPoints?: number }): boolean {
  if (/iPhone|iPad|iPod/i.test(nav.userAgent || "")) return true;
  return nav.platform === "MacIntel" && (nav.maxTouchPoints ?? 0) > 1;
}

/** Already running as the installed app (Android/desktop display mode, or iOS home-screen). */
export function isStandaloneDisplay(
  win: { matchMedia?: (query: string) => { matches: boolean } } = window,
  nav: { standalone?: boolean } = navigator as Navigator & { standalone?: boolean },
): boolean {
  if (nav.standalone === true) return true;
  const mm = win.matchMedia;
  if (typeof mm !== "function") return false;
  return ["(display-mode: standalone)", "(display-mode: fullscreen)", "(display-mode: minimal-ui)"].some(
    (q) => mm.call(win, q).matches,
  );
}

function readDismissed(storage?: Pick<Storage, "getItem">): boolean {
  try {
    const store = storage ?? window.localStorage;
    return store.getItem(INSTALL_DISMISSED_KEY) === "1";
  } catch {
    // Private mode / blocked storage: offer again next time, never crash.
    return false;
  }
}

/** "Not now" — hide the banner and remember it. The account-menu row stays. */
export function dismissInstall(storage?: Pick<Storage, "setItem">): void {
  installDismissed.value = true;
  try {
    (storage ?? window.localStorage).setItem(INSTALL_DISMISSED_KEY, "1");
  } catch {
    // Remembered for this page only.
  }
}

/** The live offer for this browser — what InstallBanner.vue renders. */
export const installOffer = computed<InstallOffer>(() =>
  installOfferFor({
    canPrompt: canInstall.value,
    ios: typeof navigator !== "undefined" && isIos(navigator),
    standalone: typeof window !== "undefined" && isStandaloneDisplay(),
    installed: installedNow.value,
    dismissed: installDismissed.value,
  }),
);

export function watchInstallPrompt(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Suppress Chrome's own mini-infobar; the shell offers its own banner.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    canInstall.value = true;
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    canInstall.value = false;
    installedNow.value = true;
  });
}

/** Re-fire the parked prompt. Must be called from a user gesture. */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferred) return "unavailable";
  const event = deferred;
  // Single-use: Chromium refuses a second `prompt()` on the same event.
  deferred = null;
  canInstall.value = false;
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === "accepted") installedNow.value = true;
    return outcome;
  } catch {
    return "dismissed";
  }
}

/** Test seam — clears the parked event and banner state between cases. */
export function resetInstallPrompt(): void {
  deferred = null;
  canInstall.value = false;
  installedNow.value = false;
  installDismissed.value = false;
}
